/**
 * Button
 * @module components/Button
 */

import { ButtonColorTypes } from 'types/common';
import clsx from 'clsx';
import { useSiteType } from 'context/siteType.context';

interface ButtonModel {
	disabled?: boolean;
	type?: 'link' | 'button' | 'submit' | 'reset';
	buttonColor: ButtonColorTypes;
	url?: string;
	onClick?: (e: React.MouseEvent<Element, MouseEvent>) => void;
	target?: string;
	tabIndex?: number;
	children?: React.ReactNode;
	className?: string;
	name?: string;
	id?: string | number;
	noMarginBottom?: string;
	noMargin?: boolean;
	[htmlAttributes: string]: any;
}
const lindabTheme: Partial<Record<ButtonColorTypes, string>> = {
	white: 'bg-white text-blue border-white hover:bg-white-hover',
	blue: 'bg-blue text-white border-blue hover:bg-blue-hover',
	grey: 'bg-transparent border border-black hover:underline',
	whiteTransparent:
		'bg-transparent border border-white hover:underline text-white',
};

const lindabGroupTheme: Partial<Record<ButtonColorTypes, string>> = {
	lgLightGrayBlue:
		'bg-lgLightGrayBlue text-black border-lgLightGrayBlue hover:bg-lgLightGrayBlue-hover',
	lgDarkBlue:
		'bg-lgDarkBlue text-lgLightGrayBlue border-lgDarkBlue hover:bg-lgDarkBlue-hover',
	lgYellow:
		'bg-lgYellow text-lgLightGrayBlue border-lgYellow hover:bg-lgYellow-hover',
	lgGreenGray:
		'bg-lgGreenGray text-lgLightGrayBlue border-lgGreenGray hover:bg-lgGreenGray-hover',
	white: 'bg-white text-greyDarkest border-white hover:bg-white-hover',
	grey: 'bg-grey text-greyDarkest border-grey hover:bg-grey-hover',
};

const Button = ({
	type,
	buttonColor,
	onClick,
	url,
	children,
	disabled,
	className,
	target,
	tabIndex,
	name,
	id,
	noMarginBottom,
	noMargin = false,
	...htmlAttributes
}: ButtonModel) => {
	const siteType = useSiteType();
	const isLindab = siteType === 'Lindab';
	const themeShape = isLindab
		? 'rounded-full px-4 py-1.5'
		: 'rounded-lg py-2 px-3 uppercase';

	const themeColor =
		(isLindab ? lindabTheme : lindabGroupTheme)[buttonColor] ?? 'text-white';
	return (
		<>
			{type === 'link' ? (
				<a
					href={url}
					onClick={onClick}
					className={clsx(
						className,
						`font-standard h-9.5 border-2 border-solid align-middle inline-flex justify-center gap-1.5 items-center text-center cursor-pointer no-underline md:text-p text-sm smMax:${noMarginBottom}`,
						'disabled:text-greyDarker disabled:bg-grey disabled:border-grey disabled:border-solid disabled:border-2 disabled:pointer-events-none',
						isLindab ? 'font-semibold' : 'font-bold',
						!noMargin && 'mb-3 mt-4',
						themeColor,
						themeShape
					)}
					target={target}
					tabIndex={tabIndex}
					id={id?.toString()}
					{...htmlAttributes}
				>
					{children}
				</a>
			) : (
				<button
					onClick={onClick}
					disabled={disabled}
					className={clsx(
						className,
						`font-standard h-9.5 border-2 border-solid align-middle inline-flex justify-center gap-1.5 items-center text-center cursor-pointer no-underline md:text-p text-sm smMax:${noMarginBottom}`,
						'disabled:text-greyDarker disabled:bg-grey disabled:border-grey disabled:border-solid disabled:border-2 disabled:pointer-events-none',
						!noMargin && 'mb-3 mt-4',
						disabled && 'hover:no-underline',
						isLindab ? 'font-semibold' : 'font-bold',
						themeColor,
						themeShape
					)}
					tabIndex={tabIndex}
					type={type || 'button'}
					name={name}
					id={id?.toString()}
					{...htmlAttributes}
				>
					{children}
				</button>
			)}
		</>
	);
};

export default Button;
